import {
  type ClientDetailTabId,
  parseClientDetailTab,
} from "@/components/clients/clientDetailTabs";
import { toAbsoluteUrl } from "@/navigation/absoluteUrl";
import type { AppLocationCodec } from "@/navigation/appLocation";
import { NAV_KEYS } from "@/navigation/keys";

export type ClientDetailLocation = {
  faClientId: string;
  tab: ClientDetailTabId;
  taskId?: string;
};

export function clientDetailDefaults(faClientId = ""): ClientDetailLocation {
  return { faClientId, tab: "overview" };
}

function readTaskId(params: Record<string, string | undefined>): string | undefined {
  const canonical = params[NAV_KEYS.taskId]?.trim();
  if (canonical) return canonical;
  const legacy = params[NAV_KEYS.taskLegacy]?.trim();
  return legacy || undefined;
}

function parseFaClientIdFromPath(pathname: string): string | undefined {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "clients" || !segments[1]) return undefined;
  return decodeURIComponent(segments[1]);
}

export function normalizeClientDetail(loc: ClientDetailLocation): ClientDetailLocation {
  return {
    faClientId: loc.faClientId,
    tab: loc.tab,
    ...(loc.taskId ? { taskId: loc.taskId } : {}),
  };
}

function fromParams(params: Record<string, string | undefined>): ClientDetailLocation {
  const faClientId = params.faClientId?.trim() ?? "";
  const base = clientDetailDefaults(faClientId);
  const tab = parseClientDetailTab(params[NAV_KEYS.tab]);
  if (tab) base.tab = tab;
  const taskId = readTaskId(params);
  if (taskId) base.taskId = taskId;
  return normalizeClientDetail(base);
}

function fromUrl(pathname: string, searchParams: URLSearchParams): ClientDetailLocation {
  const params: Record<string, string | undefined> = Object.fromEntries(searchParams);
  const pathId = parseFaClientIdFromPath(pathname);
  if (pathId) params.faClientId = pathId;
  return fromParams(params);
}

function toParams(loc: ClientDetailLocation): Record<string, string> {
  const n = normalizeClientDetail(loc);
  const d = clientDetailDefaults(n.faClientId);
  const out: Record<string, string> = {};
  if (n.faClientId) out.faClientId = n.faClientId;
  if (n.tab !== d.tab) out[NAV_KEYS.tab] = n.tab;
  if (n.taskId) out[NAV_KEYS.taskId] = n.taskId;
  return out;
}

function toUrl(loc: ClientDetailLocation): string {
  const n = normalizeClientDetail(loc);
  const path = `/clients/${encodeURIComponent(n.faClientId)}`;
  const d = clientDetailDefaults(n.faClientId);
  const query: Record<string, string> = {};
  if (n.tab !== d.tab) query[NAV_KEYS.tab] = n.tab;
  if (n.taskId) query[NAV_KEYS.taskId] = n.taskId;
  const qs = new URLSearchParams(query).toString();
  return qs ? `${path}?${qs}` : path;
}

export const clientDetailCodec: AppLocationCodec<ClientDetailLocation> = {
  appId: "client",
  fromUrl,
  fromParams,
  toParams,
  toUrl,
};

export function buildClientDetailWindowUrl(
  faClientId: string,
  loc: Partial<Omit<ClientDetailLocation, "faClientId">> = {},
): string {
  return clientDetailCodec.toUrl({ ...clientDetailDefaults(faClientId), ...loc });
}

export function clientDetailShareUrl(faClientId: string, loc: Partial<Omit<ClientDetailLocation, "faClientId">>): string {
  return toAbsoluteUrl(buildClientDetailWindowUrl(faClientId, loc));
}
